import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class FileResizeService {

    async resizeIfNeeded(file: File, maxWidth: number, maxHeight: number, maxMB: number): Promise<File> {
        const __mime = file.type;

        if (__mime === 'image/svg+xml' || __mime === 'image/gif')
            return file;

        // Eén keer decoderen (robuust). Lukt decoderen niet — bv. een HEIC/HEIF-foto uit een
        // telefoon-galerij of een formaat dat de browser niet aankan — dan uploaden we het
        // origineel ongewijzigd; de backend maakt zelf de varianten/thumbnails.
        const __bitmap = await __decode(file);
        if (!__bitmap)
            return file;

        const __srcWidth  = __bitmap.width;
        const __srcHeight = __bitmap.height;

        const __exceedsDimensions = __srcWidth > maxWidth || __srcHeight > maxHeight;
        const __exceedsMB         = file.size / (1024 * 1024) > maxMB;

        if (!__exceedsDimensions && !__exceedsMB) {
            __bitmap.close();
            return file;
        }

        const { width: __w, height: __h } = __calcDimensions(__srcWidth, __srcHeight, maxWidth, maxHeight);

        const __canvas = document.createElement('canvas');
        __canvas.width  = __w;
        __canvas.height = __h;
        __canvas.getContext('2d')!.drawImage(__bitmap, 0, 0, __w, __h);
        __bitmap.close();

        const __blob = await __toBlob(__canvas, __mime);
        return new File([__blob], file.name, { type: __blob.type });
    }
}

// Decodeer een bestand naar een ImageBitmap. Eerst mét EXIF-rotatie (imageOrientation:
// 'from-image' geeft de bitmap meteen de juiste oriëntatie); valt die optie weg (oudere
// browsers) dan zonder optie; lukt decoderen helemaal niet → null (caller uploadt origineel).
async function __decode(file: File): Promise<ImageBitmap | null> {
    try {
        return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
        try {
            return await createImageBitmap(file);
        } catch {
            return null;
        }
    }
}

function __calcDimensions(srcW: number, srcH: number, maxW: number, maxH: number): { width: number; height: number } {
    let __w = srcW;
    let __h = srcH;
    if (__w > maxW) { __h = Math.round(__h * maxW / __w); __w = maxW; }
    if (__h > maxH) { __w = Math.round(__w * maxH / __h); __h = maxH; }
    return { width: __w, height: __h };
}

function __toBlob(canvas: HTMLCanvasElement, mime: string): Promise<Blob> {
    const __quality = mime === 'image/jpeg' ? 0.92 : undefined;
    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
            if (blob) { resolve(blob); return; }
            if (mime !== 'image/webp') { reject(new Error('canvas.toBlob mislukt')); return; }
            // WebP niet ondersteund (iOS Safari) → PNG fallback
            canvas.toBlob(fallback => {
                if (fallback) resolve(fallback);
                else reject(new Error('canvas.toBlob mislukt'));
            }, 'image/png');
        }, mime, __quality);
    });
}
